import { useRouter } from 'next/router'
import { signIn } from 'next-auth/react'
import { useTranslation } from 'next-i18next'
import { useContext, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { PasswordInput } from 'components/Form/PasswordInput'
import { UserActionsContext } from 'components/User/UserActionsContext'
import { Button } from 'components/common/Button'
import { ButtonVariant } from 'components/common/Button/ButtonVariant'
import { Loader } from 'components/common/Loader'
import { TextInput } from 'components/common/TextInput'
import { apiClient } from 'services/api/client'
import { setInLocalStorage } from 'services/localStorage/getOrSetLocalStorage'
import { AuthModalContext } from './AuthModalContext'
import { SignUpFormValues } from './SignUpFormValues'

export const SignUpForm = () => {
  const {
    modalParams: { redirectUrl },
    setModalParams
  } = useContext(AuthModalContext)
  const [isLoading, setIsLoading] = useState(false)
  const { eventId } = useContext(UserActionsContext)
  const router = useRouter()

  const onSubmit = async ({
    firstName,
    lastName,
    email,
    password
  }: SignUpFormValues) => {
    setIsLoading(true)

    await apiClient.post<SignUpFormValues>('auth/sign-up', {
      firstName,
      lastName,
      email,
      password,
      language: router.locale
    })

    void signIn('email', {
      email,
      callbackUrl: eventId
        ? `/api/event/save-event-and-redirect?eventId=${eventId}&redirectTo=${encodeURI(
            redirectUrl
          )}`
        : redirectUrl,
      redirect: false
    })

    setIsLoading(false)
    setInLocalStorage('user.email', email)
    setInLocalStorage('user.firstName', firstName)
    setInLocalStorage('user.lastName', lastName)
    setModalParams({
      state: 'WELCOME',
      redirectUrl
    })
  }

  const {
    handleSubmit,
    formState: { errors },
    register
  } = useFormContext<SignUpFormValues>()
  const { t } = useTranslation('authentication')

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        width: '100%'
      }}
    >
      <TextInput
        fullSize
        inputProps={{
          ...register('firstName'),
          style: { textTransform: 'capitalize' },
          autoComplete: 'given-name',
          required: true
        }}
        inputLabel={t('register.form.firstName.label')}
        errorMessage={errors.firstName?.message}
      />
      <TextInput
        fullSize
        inputProps={{
          ...register('lastName'),
          style: { textTransform: 'uppercase' },
          autoComplete: 'family-name',
          required: true
        }}
        inputLabel={t('register.form.lastName.label')}
        errorMessage={errors.lastName?.message}
      />
      <TextInput
        fullSize
        inputProps={{
          ...register('email'),
          autoComplete: 'email',
          required: true
        }}
        inputLabel={t('register.form.email.label')}
        errorMessage={errors.email?.message}
      />
      <PasswordInput<SignUpFormValues>
        register={register}
        name="password"
        errors={errors}
      />
      <Button
        variant={ButtonVariant.Primary}
        fullWidth
        label={t('register.button.submit')}
        leftIcon={isLoading ? <Loader /> : undefined}
        disabled={isLoading}
        type="submit"
      />
    </form>
  )
}
